import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { z } from "zod"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { authClient } from "../lib/auth/authClient"
import { apiFetch } from "../utils/apiFetch"

export const Route = createFileRoute("/shareTemplate")({
  component: ShareTemplate,
  validateSearch: z.object({ token: z.string().optional() }),
})

type PreviewData = {
  templateName: string
  color: string
  exerciseNames: string[]
  sharerName: string
}

type UIState = "loading" | "preview" | "invalid" | "accepting" | "accepted"

function ShareTemplate() {
  const { token } = Route.useSearch()
  const navigate = useNavigate()
  const [uiState, setUiState] = useState<UIState>("loading")
  const [preview, setPreview] = useState<PreviewData | null>(null)
  const [, setNewTemplateId] = useState<string | null>(null)

  useEffect(() => {
    if (!token) {
      setUiState("invalid")
      return
    }

    const apiUrl = import.meta.env.VITE_API_URL as string
    fetch(`${apiUrl}/share/preview/${token}`)
      .then(async (res) => {
        if (!res.ok) {
          setUiState("invalid")
          return
        }
        const data = (await res.json()) as PreviewData
        setPreview(data)
        setUiState("preview")
      })
      .catch(() => setUiState("invalid"))
  }, [token])

  const handleAccept = async () => {
    const { data: session } = await authClient.getSession()
    if (!session) {
      navigate({
        to: "/login",
        search: { redirect: `/shareTemplate?token=${token}` },
      })
      return
    }

    setUiState("accepting")
    try {
      const result = await apiFetch<{ workoutTemplateId: string }>(`/share/accept/${token}`, {
        method: "POST",
      })
      setNewTemplateId(result.workoutTemplateId)
      setUiState("accepted")
    } catch {
      setUiState("invalid")
    }
  }

  if (uiState === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center text-muted-foreground">Loading...</CardContent>
        </Card>
      </div>
    )
  }

  if (uiState === "invalid") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Invalid or Expired Link</CardTitle>
            <CardDescription>This share link is invalid, has already been used, or has expired.</CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            <Link to="/login">
              <Button>Sign In</Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    )
  }

  if (uiState === "accepted") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Template Saved!</CardTitle>
            <CardDescription>"{preview?.templateName}" has been added to your templates.</CardDescription>
          </CardHeader>
          <CardFooter className="justify-center">
            <Link to="/templates">
              <Button>View My Templates</Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mb-2 flex justify-center">
            <img src="/openiron.png" alt="OpenIron Logo" className="h-12 w-12" />
          </div>
          <CardTitle>{preview?.templateName}</CardTitle>
          <CardDescription>{preview?.sharerName} wants to share this workout template with you.</CardDescription>
        </CardHeader>
        <CardContent>
          {preview?.color && (
            <div className="mb-3 h-1.5 w-full rounded-full" style={{ backgroundColor: preview.color }} />
          )}
          <h3 className="mb-2 font-medium text-sm">Exercises</h3>
          <ul className="space-y-1">
            {preview?.exerciseNames.map((name, i) => (
              <li key={i} className="text-muted-foreground text-sm">
                {i + 1}. {name}
              </li>
            ))}
          </ul>
        </CardContent>
        <CardFooter className="justify-center">
          <Button onClick={handleAccept} disabled={uiState === "accepting"}>
            {uiState === "accepting" ? "Saving..." : "Accept & Save to My Templates"}
          </Button>
        </CardFooter>
      </Card>
    </div>
  )
}
